import { FC, useCallback, useEffect, useRef, useState } from "react";

import { Box, Stack, StackProps, useTheme } from "@mui/material";

import { AmplitudeEvents } from "../../../constants/amplitude";
import { useAmplitude } from "../../../hooks/amplitude/useAmplitude";

import { useExpandableContentHeight } from "./useExpandableContentHeight";

import { FormButton } from "@/forms-design-system/FormButton";
import { FormTextButton } from "@/forms-design-system/FormTextButton";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useTranslate } from "@/hooks/useTranslate";
import { ChevronDown } from "@/icons/outlined";

export type FormExpandableTextProps = Omit<StackProps, "maxHeight" | "children"> & {
  maxHeight: StackProps["maxHeight"];
  gradientOverlayHeight: StackProps["height"];
  gradientOverlayColor?: string;
  isInitiallyExpanded?: boolean;
  children?: string | null;
  useTextButton?: boolean;
  amplitudeEventType?: "form" | "item";
};

export const FormExpandableText: FC<FormExpandableTextProps> = ({
  children,
  maxHeight,
  isInitiallyExpanded = false,
  gradientOverlayHeight,
  gradientOverlayColor = "white",
  useTextButton = false,
  amplitudeEventType = "form",
  ...rest
}) => {
  const theme = useTheme();
  const { isSmallScreen } = useMediaQuery();
  const { t } = useTranslate();
  const [isExpanded, setIsExpanded] = useState(isInitiallyExpanded);
  const contentRef = useRef<HTMLDivElement>(null);
  const { logAmplitudeEvent } = useAmplitude();
  const { isCropped: canBeExpanded, height, refresh } = useExpandableContentHeight(contentRef);

  const showExpandedVersion = !canBeExpanded || isExpanded;

  useEffect(() => {
    // Refresh the height when the children change
    refresh();
  }, [children, refresh]);

  const handleOnToggle = useCallback(
    (newValue: boolean) => {
      const amplitudeEvent =
        amplitudeEventType === "item"
          ? AmplitudeEvents.DonorFormTicketExpanded
          : AmplitudeEvents.DonorFormDescriptionExpanded;

      logAmplitudeEvent(amplitudeEvent, {
        value: isExpanded ? "less details" : "more details",
      });
      setIsExpanded(newValue);
      refresh();
    },
    [refresh, logAmplitudeEvent, isExpanded, amplitudeEventType]
  );

  useEffect(() => {
    setIsExpanded(isInitiallyExpanded);
  }, [isInitiallyExpanded]);

  if (!children) {
    return null;
  }

  if (!theme.palette.forms) {
    return <InvalidThemeErrorMessage />;
  }

  return (
    <Stack position="relative" alignItems="flex-start" gap={{ xs: 0.5, md: useTextButton ? 0.5 : 2 }} {...rest}>
      {/* This content isn't shown, it's just used to calculate the height */}
      <Box
        ref={contentRef}
        aria-hidden="true"
        sx={{
          position: "absolute",
          maxHeight,
          overflow: "hidden",
          width: "100%",
          maxWidth: "100%",
          opacity: 0,
          pointerEvents: "none",
          ...theme.typography.body2,
          ["& *"]: {
            maxWidth: "100%",
          },
          ["& p"]: {
            width: "auto !important",
            marginInline: "0 !important",
          },
        }}
        dangerouslySetInnerHTML={{ __html: children }}
      />
      <Box
        position="relative"
        sx={{
          height: showExpandedVersion ? height : maxHeight,
          transition: "height 0.3s ease-in-out",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            ...theme.typography.body2,
            color: theme.palette.forms.text.moderate,
            maxWidth: "100%",
            ["& *"]: {
              maxWidth: "100%",
              color: "inherit !important",
            },
            ["& p"]: {
              width: "auto !important",
              marginInline: "0 !important",
            },
          }}
          dangerouslySetInnerHTML={{ __html: children }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: gradientOverlayHeight,
            background: `linear-gradient(to bottom, transparent, ${gradientOverlayColor} 100%)`,
            pointerEvents: "none",
            opacity: showExpandedVersion ? 0 : 1,
            transition: "opacity 300ms linear",
          }}
        />
      </Box>
      {canBeExpanded && (
        <>
          {isSmallScreen || useTextButton ? (
            <FormTextButton onClick={() => handleOnToggle(!isExpanded)}>
              {isExpanded ? t("common", "seeLessDetailsMobile") : t("common", "seeMoreDetailsMobile")}
            </FormTextButton>
          ) : (
            <FormButton
              variant="outline"
              size="small"
              onClick={() => handleOnToggle(!isExpanded)}
              endIcon={<ChevronDown />}
              components={{
                endIcon: {
                  sx: {
                    transition: "rotate 200ms ease-in-out",
                    rotate: isExpanded ? "180deg" : "0deg",
                  },
                },
              }}
            >
              {isExpanded ? t("common", "seeLessDetails") : t("common", "seeMoreDetails")}
            </FormButton>
          )}
        </>
      )}
    </Stack>
  );
};
